import { Info, NotificationService, useIntl } from '@capsicum/common';
import {
	Alert,
	Button,
	Divider,
	GenericDrawer,
	Image,
	ModalService,
	Snackbar,
	useGoatImage,
	useIdParam,
	useRouter
} from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import { ORDER_FIELD_MAPPINGS, useProductPage } from '@capsicum/features/fanshop/shared';
import { useMe } from '@capsicum/socios-core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import styled from 'styled-components';

export type PlaceOrderProps = {
	params: { id?: string };
};

export const PlaceOrder = ({ params }: PlaceOrderProps) => {
	const { $t } = useIntl();
	const router = useRouter();
	const { me } = useMe();

	// Custom hooks
	const { id } = useIdParam(params, '/fan-shop/rewards');

	const [isSuccessModalShown, setIsSuccessModalShown] = useState<boolean>(false);

	const GoatSoldImage = useGoatImage('sold');

	const {
		completeCheckoutData,
		faqLink,
		fieldsWithEmptyValuesError,
		getCategory,
		getFieldByCategory,
		hasLinkedProduct,
		hasLinkedProductOrder,
		isAuction,
		isExperienceType,
		isMerchandiseType,
		isTicketType,
		isVideoGreetingsType,
		mainBlurHash,
		mainImage,
		merchandiseDetails,
		orderPlaced,
		placeOrder,
		quantity,
		recipientName,
		setOrderPlaced,
		texts,
		ticketHolders,
		totalPrice,
		variant
	} = useProductPage({
		variantId: id as string
	});

	useEffect(() => {
		if (fieldsWithEmptyValuesError) {
			NotificationService.addNotification({
				component: (
					<Snackbar
						description={texts.fieldsWithEmptyValuesError}
						testID="placeOrder-fieldsWithEmptyValuesError-snackbar"
						variant="error"
					/>
				)
			});
		}
	}, [fieldsWithEmptyValuesError, texts.fieldsWithEmptyValuesError]);

	const confirmDrawer = useCallback(() => {
		return ModalService.addModal({
			detached: false,
			renderComponent: ({ close }) => (
				<GenericDrawer
					buttonText={$t('rewards.fanshop.linkedProductsModal.buttonText')}
					description={$t('rewards.fanshop.linkedProductsModal.message')}
					image={Info}
					testID="place-order-confirmation-drawer"
					title={$t('rewards.fanshop.linkedProductsModal.title')}
					onButtonPress={async () => {
						await close();
						await placeOrder();
					}}
				>
					<S.StyledDrawerText>
						{isTicketType || isExperienceType
							? $t('rewards.fanshop.linkedProductsModal.messageConfirmationTicket')
							: $t('rewards.fanshop.linkedProductsModal.messageConfirmationProduct')}
					</S.StyledDrawerText>
				</GenericDrawer>
			)
		});
	}, []);

	useEffect(() => {
		if (completeCheckoutData && !isSuccessModalShown) {
			setIsSuccessModalShown(true);
			ModalService.addModal({
				isClosable: false,
				renderComponent: ({ close }) => (
					<GenericDrawer
						buttonText={texts.goToFanShop}
						description={texts.confirmationMessage}
						image={GoatSoldImage}
						testID="place-order-processing-drawer"
						title={`#${completeCheckoutData?.data?.referenceNumber}`}
						onButtonPress={() => {
							router.push('/fan-shop/rewards');
							setOrderPlaced(false);

							return close();
						}}
					/>
				)
			});
		}
	}, [
		GoatSoldImage,
		completeCheckoutData,
		isSuccessModalShown,
		router,
		texts.confirmationMessage,
		texts.goToFanShop
	]);

	const informationTicketBanner = useMemo(
		() =>
			!hasLinkedProductOrder &&
			hasLinkedProduct && (
				<Alert
					alignIcon="flex-start"
					closable={false}
					icon={InfoIcon}
					subTitle={
						isTicketType || isExperienceType
							? $t('rewards.fanshop.linkedProductsBanner.informationTicketText')
							: $t('rewards.fanshop.linkedProductsBanner.informationDefaultText')
					}
					testID="place-order-information-banner"
					title={$t('rewards.fanshop.linkedProductsBanner.title')}
					type="message"
				/>
			),
		[$t, hasLinkedProductOrder, hasLinkedProduct, isTicketType, isExperienceType]
	);

	const imageSection = useMemo(
		() => (
			<S.ImageContainer>
				{mainImage ? (
					<S.StyledCardImage
						alt={`${variant?.label} image`}
						blurhash={mainBlurHash}
						height={303}
						optimized
						src={mainImage}
						width={256}
					/>
				) : (
					<S.StyledCardImagePlaceholder />
				)}
			</S.ImageContainer>
		),
		[mainBlurHash, mainImage, variant?.label]
	);

	const videoGreetingsSection = useMemo(
		() =>
			isVideoGreetingsType ? (
				<>
					<S.StyledDivider orientation="horizontal" />
					<S.InfoTitle>{texts.recipientName}</S.InfoTitle>
					<S.InfoText>{recipientName}</S.InfoText>
				</>
			) : null,
		[isVideoGreetingsType, recipientName, texts.recipientName]
	);

	const warningSection = useMemo(
		() =>
			!isAuction ? (
				<>
					<S.StyledDivider orientation="horizontal" />
					<S.TotalTitle>{texts.total}</S.TotalTitle>
					<S.TotalText>{`${totalPrice} ${texts.rewardPoints}`}</S.TotalText>
					<S.StyledDivider orientation="horizontal" />
					<S.InformationWrapper>{informationTicketBanner}</S.InformationWrapper>
					<S.InfoTitle>{texts.warningText}</S.InfoTitle>
					<S.InfoWarning>{texts.proofOfTravel}</S.InfoWarning>
					<S.StyledLinkText data-testid="place-order-faq-link" href={faqLink} target="_blank">
						{texts.faq}
					</S.StyledLinkText>
				</>
			) : null,
		[
			faqLink,
			informationTicketBanner,
			isAuction,
			texts.faq,
			texts.proofOfTravel,
			texts.rewardPoints,
			texts.total,
			texts.warningText,
			totalPrice
		]
	);

	const handlePlaceOrderClick = useCallback(() => {
		if (!hasLinkedProductOrder && hasLinkedProduct) {
			confirmDrawer();
		} else {
			placeOrder();
		}
	}, [confirmDrawer, hasLinkedProduct, hasLinkedProductOrder, placeOrder]);

	return (
		<S.Wrapper>
			<S.CategoryTitle>{texts.titlePlaceOrder}</S.CategoryTitle>
			<S.PlaceOrderWrapper>
				{imageSection}
				<S.DetailsWrapper>
					<S.InfoTitle>{texts.deliveryAddress}</S.InfoTitle>
					<S.InfoText>{`${getFieldByCategory(ORDER_FIELD_MAPPINGS.firstName, me.firstName || '')} ${getFieldByCategory(ORDER_FIELD_MAPPINGS.lastName, me.lastName || '')}`}</S.InfoText>
					<S.InfoText>{getFieldByCategory(ORDER_FIELD_MAPPINGS.email, me.email || '')}</S.InfoText>

					{videoGreetingsSection}

					{isMerchandiseType && <S.InfoText>{merchandiseDetails()}</S.InfoText>}

					<S.StyledDivider orientation="horizontal" />

					{isTicketType &&
						ticketHolders()?.map((item, index) => (
							<div key={`ticket-${index}`}>
								<S.InfoTitle>
									{$t('rewards.fanshop.placeOrder.ticketHolder', { index: index + 1 })}
								</S.InfoTitle>
								<S.InfoText>{`${item.firstname} ${item.lastname}`}</S.InfoText>
								<S.StyledDivider orientation="horizontal" />
							</div>
						))}

					<S.Category>{getCategory(variant)}</S.Category>
					<S.Title>{variant?.label}</S.Title>
					<S.ItemAmountWrapper>
						<S.ItemAmount>{texts.quantity}</S.ItemAmount>
						<S.ItemAmountQuantity>{quantity}</S.ItemAmountQuantity>
					</S.ItemAmountWrapper>
					{warningSection}
					<S.ButtonWrapper>
						<Button
							fullWidth={isMobile}
							isLoading={orderPlaced}
							testID="place-order-redeem-button"
							text={texts.redeemButton}
							onClick={handlePlaceOrderClick}
						/>
					</S.ButtonWrapper>
				</S.DetailsWrapper>
			</S.PlaceOrderWrapper>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		width: 100%;
		display: flex;
		flex-direction: column;
	`;

	export const PlaceOrderWrapper = styled.div`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		display: grid;
		grid-template-columns: calc(50% - 10px) calc(50% - 10px);
		grid-column-gap: ${({ theme }) => theme.spacings.small};
		padding: ${({ theme }) => theme.spacings.small};
		${({ theme }) => theme.border.radius.medium};
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			display: flex;
			flex-direction: column;
			margin-bottom: 70px;
		}
	`;

	export const CategoryTitle = styled.div`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: ${({ theme }) => theme.spacings.xxlarge} 0 ${({ theme }) => theme.spacings.large};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin-left: ${({ theme }) => theme.spacings.small};
		}
	`;

	export const ImageContainer = styled.div`
		display: flex;
		flex-direction: column;
		width: 100%;
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			margin-bottom: ${({ theme }) => theme.spacings.medium};
		}
	`;

	export const StyledCardImage = styled(Image)`
		object-fit: contain;
		width: 100%;
		margin: 0 auto;
		${({ theme }) =>
			theme.breakpoints.queries.builder.or(
				theme.breakpoints.queries.above.mediumDesktop,
				theme.breakpoints.queries.only.mobile
			)} {
		}
	`;

	export const StyledCardImagePlaceholder = styled.div`
		height: 254px;
		width: 256px;
		background-color: ${({ theme }) => theme.colors.skeleton.default};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			width: 100%;
		}
	`;

	export const DetailsWrapper = styled.div`
		display: flex;
		flex-direction: column;
		flex: 1;
	`;

	export const InformationWrapper = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const InfoTitle = styled.div`
		${({ theme }) => theme.fonts.text.text1};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const InfoText = styled.div`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const StyledDivider = styled(Divider)`
		margin: ${({ theme }) => theme.spacings.small} 0;
	`;

	export const Category = styled.div`
		${({ theme }) => theme.fonts.text.caption1};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Title = styled.div`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ItemAmountWrapper = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
		column-gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const ItemAmount = styled.div`
		${({ theme }) => theme.fonts.text.text1}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ItemAmountQuantity = styled.div`
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const TotalTitle = styled.div`
		${({ theme }) => theme.fonts.text.caption1};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const TotalText = styled.div`
		${({ theme }) => theme.fonts.headings.title5}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const InfoWarning = styled.div`
		margin: ${({ theme }) => theme.spacings.small} 0;
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const StyledLinkText = styled.a`
		margin-bottom: ${({ theme }) => theme.spacings.medium};
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;

	export const ButtonWrapper = styled.div`
		align-self: end;

		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			position: fixed;
			width: 100%;
			bottom: 0;
			left: 0;
			margin-top: 0;
			padding: ${({ theme }) => theme.spacings.small};
			background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
			display: flex;
			flex-direction: row;
			align-self: center;
		}
	`;

	export const StyledDrawerText = styled.p`
		text-align: center;
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
}
